import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { LABELS } from "../../services/constants/Rules";
import { Grid, Popover, Box, Divider, Typography } from "@mui/material";
import { IPieData } from "../../types/reports";
import { useNavigate } from "react-router-dom";
import { formatNumberWithCommas } from "../../services/utils";
import {
  IconDbInfo,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import {
  brand,
  navyScale,
  scssTokens,
  darkModeTokens,
  getPremiumResourceCardSx,
} from "../../styles/Theme";

ChartJS.register(ArcElement, Tooltip, Legend);

const MIN_PERCENTAGE = 0.5;

const ColorBox = ({ color }: { color: string }) => (
  <label
    style={{
      width: "11px",
      height: "11px",
      backgroundColor: color,
      display: "inline-block",
      borderRadius: "2px",
      flexShrink: 0,
    }}
  />
);

const PieChartComponent = ({ counts }: { counts: any }) => {
  const allCountsZero =
    counts.match === 0 &&
    counts.mismatch === 0 &&
    counts.missing === 0 &&
    counts.duplicate === 0;

  const total =
    counts.match + counts.mismatch + counts.missing + counts.duplicate;

  const matchPercentage = (counts.match / total) * 100 || 0;
  const mismatchPercentage = (counts.mismatch / total) * 100 || 0;
  const missingPercentage = (counts.missing / total) * 100 || 0;
  const duplicatePercentage = (counts.duplicate / total) * 100 || 0;
  const totalPercentage = 100;

  const adjustedMatchPercentage = {
    value:
      matchPercentage > 0 && matchPercentage < MIN_PERCENTAGE
        ? MIN_PERCENTAGE
        : matchPercentage,
    orignalValue: counts.match,
    label: "Match",
  };
  const adjustedMismatchPercentage = {
    value:
      mismatchPercentage > 0 && mismatchPercentage < MIN_PERCENTAGE
        ? MIN_PERCENTAGE
        : mismatchPercentage,
    orignalValue: counts.mismatch,
    label: "Mismatch",
  };
  const adjustedMissingPercentage = {
    value:
      missingPercentage > 0 && missingPercentage < MIN_PERCENTAGE
        ? MIN_PERCENTAGE
        : missingPercentage,
    orignalValue: counts.missing,
    label: "Missing",
  };
  const adjustedDuplicatePercentage = {
    value:
      duplicatePercentage > 0 && duplicatePercentage < MIN_PERCENTAGE
        ? MIN_PERCENTAGE
        : duplicatePercentage,
    orignalValue: counts.duplicate,
    label: "Duplicate",
  };
  const adjustedTotalPercentage = {
    value:
      totalPercentage > 0 && totalPercentage < MIN_PERCENTAGE
        ? MIN_PERCENTAGE
        : totalPercentage,
    orignalValue: [counts.total],
    label: "Total",
  };

  const dataValues = allCountsZero
    ? [adjustedTotalPercentage]
    : [
        adjustedMatchPercentage,
        adjustedMismatchPercentage,
        adjustedMissingPercentage,
        adjustedDuplicatePercentage,
      ];

  const data = {
    datasets: [
      {
        data: dataValues,
        backgroundColor: allCountsZero
          ? ["#B3B3B3"]
          : ["#74B856", "#E84336", "#add8e6", "#f7b84b"],
        borderColor: allCountsZero
          ? ["#B3B3B3"]
          : ["#74B856", "#E84336", "#add8e6", "#f7b84b"],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.raw.label || "";
            const value = context.raw.orignalValue || 0;
            const digits =
              value === 0 ? 1 : Math.floor(Math.log10(Math.abs(value))) + 1;
            const isLargeNumber = digits < 6;
            return isLargeNumber
              ? `${label}: ${formatNumberWithCommas(value)}`
              : [`${label}:`, `${formatNumberWithCommas(value)}`];
          },
        },
      },
    },
  };

  return allCountsZero && counts.total === 0 ? (
    <svg
      width="95%"
      height="95%"
      viewBox="0 0 252 252"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="126" cy="126" r="126" fill="#B3B3B3" />
    </svg>
  ) : (
    <Pie data={data} options={options} />
  );
};

interface IComparisonDashboardCardProps {
  pieData: IPieData;
  domainId?: number;
  skipDuplicateRecords?: boolean;
  totalMatchRecords?: number;
}

const ComparisonDashboardCard: React.FC<IComparisonDashboardCardProps> = ({
  pieData,
  domainId,
  skipDuplicateRecords,
  totalMatchRecords,
}) => {
  console.log("pieData", pieData);
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const [dbInfoAnchorEl, setDbInfoAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const handleDbInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setDbInfoAnchorEl(event.currentTarget);
  };
  const handleDbInfoClose = () => {
    setDbInfoAnchorEl(null);
  };
  const isDbInfoPopoverOpen = Boolean(dbInfoAnchorEl);

  const hasIssues =
    (pieData.total_mismatched_records || 0) > 0 ||
    (pieData.total_missing_records || 0) > 0;

  // const matchedCount =
  //   (pieData.total_common_records || 0) -
  //   (pieData.total_mismatched_records || 0);

  const totalRecords = pieData.total_records || 0;
  const safeTotalMatchRecords = totalMatchRecords ?? 0;
  const successRateVal =
    totalRecords > 0 ? (safeTotalMatchRecords / totalRecords) * 100 : 0;
  const successRate = successRateVal.toFixed(1);
  // DQ-8674 / dark mode: success color — teal on pass, error red on fail.
  // In dark mode the danger red is lightened to #ef6e6e (--error dark token)
  // so it reads against the navy card surface.
  const successRateColor =
    successRateVal >= 100
      ? brand.teal
      : isDark
        ? "#ef6e6e"
        : scssTokens.badgeDangerBg;

  // Mode-aware border / text tokens used throughout the card
  const dividerColor = isDark
    ? darkModeTokens.border // "rgba(215,241,255,0.12)"
    : "rgba(83,126,160,0.15)"; // blueScale[600] 15%
  const mutedTextColor = isDark
    ? scssTokens.lightGrey // #a7a8af (navyScale[200]) — readable on dark
    : "#64748B"; // slate grey on light bg
  const totalLegendDotColor = isDark
    ? navyScale[300] // #7b7d88 — visible on dark surface
    : "#9e9e9e";

  const dataLabels = LABELS.filter((l) => l.label !== "Total");

  return (
    <Box
      sx={{
        ...getPremiumResourceCardSx(mode),
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header: resource name + status icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          pb: 1,
          borderBottom: `1px solid ${dividerColor}`,
        }}
      >
        <Box
          sx={{ flex: "1 1 auto", minWidth: 0, cursor: "pointer" }}
          onClick={() => {
            navigate(`/resource/${domainId}/view/${pieData.resource_id}`);
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            // brand.navy is invisible on dark bg — swap to brand.ice
            sx={{ color: isDark ? brand.ice : brand.navy }}
          >
            {pieData.resource_name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {pieData && pieData.are_all_errors_stored === false && (
            <span className="popover-box">
              <span
                className="db-info-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDbInfoClick(e);
                }}
                style={{ cursor: "pointer", display: "flex" }}
              >
                <IconDbInfo />
              </span>
              <Popover
                open={isDbInfoPopoverOpen}
                anchorEl={dbInfoAnchorEl}
                onClose={handleDbInfoClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { className: "db-info-popover" } }}
              >
                <Box className="db-error-popover-body">
                  <div className="title">Records summary</div>
                  <ul>
                    <li>
                      <div className="item-parent">
                        <div className="item1">
                          Filtered records by null values :{" "}
                        </div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.filtered_records_by_null_values_in_unique_keys ||
                              0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.filtered_records_by_null_values_in_unique_keys_stored_in_db ||
                              0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total adhoc query results :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_cross_field_validation_results || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_cross_field_validation_results_stored_in_db ||
                              0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total duplicate records :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_duplicate_records || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_duplicate_records_stored_in_db || 0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total validation errors :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_validation_errors || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            pieData?.total_validation_errors_stored_in_db || 0,
                          )}
                        </div>
                      </div>
                    </li>
                  </ul>
                </Box>
              </Popover>
            </span>
          )}
          {hasIssues ? (
            <IconCrossCircleIconSvg width={20} height={20} />
          ) : (
            <IconCheckCircleIconGreenSvg width={20} height={20} />
          )}
        </Box>
      </Box>

      {/* Pie chart (left) + Legend (right) */}
      <Grid container alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={5}>
          <Box sx={{ width: "100%", maxWidth: 140, mx: "auto" }}>
            <PieChartComponent counts={pieData.countsData} />
          </Box>
        </Grid>
        <Grid item xs={7}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 0.75, pl: 1 }}
          >
            {dataLabels.map((label) => {
              const count = pieData.countsData[label.label.toLowerCase()] || 0;
              if (count === 0 && label.label !== "Match") return null;
              return (
                <Box
                  key={label.label}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <ColorBox color={label.color} />
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {label.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ ml: "auto", fontSize: "0.8rem" }}
                  >
                    {formatNumberWithCommas(count)}
                  </Typography>
                </Box>
              );
            })}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pt: 0.25,
                borderTop: `1px dashed ${isDark ? "rgba(215,241,255,0.10)" : "rgba(83,126,160,0.20)"}`,
                mt: 0.25,
              }}
            >
              <ColorBox color={totalLegendDotColor} />
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                Total
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ ml: "auto", fontSize: "0.8rem" }}
              >
                {formatNumberWithCommas(pieData.total_records)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Divider */}
      <Divider sx={{ borderColor: dividerColor, my: 1 }} />

      {/* Summary stats list */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>Total Common Records</span>
          <span>{formatNumberWithCommas(pieData.total_common_records)}</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>Total Unique Records</span>
          <span>{formatNumberWithCommas(pieData.total_unique_records)}</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>
            Total Duplicate Records
            {skipDuplicateRecords === true ? " (removed)" : ""}
          </span>
          <span>{formatNumberWithCommas(pieData.total_duplicate_records)}</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>Total Null Key Records</span>
          <span>{formatNumberWithCommas(pieData.total_null_key_records)}</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>False Positive Missing Count</span>
          <span>
            {formatNumberWithCommas(
              pieData.false_positive_missing_records_count,
            )}
          </span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>Filtered Records By Rule Filters</span>
          <span>
            {formatNumberWithCommas(
              pieData.filtered_records_by_rule_filters ?? 0,
            )}
          </span>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mutedTextColor,
          }}
        >
          <span>Filtered Records By Resource Filters</span>
          <span>
            {formatNumberWithCommas(
              pieData.filtered_records_by_resource_filters,
            )}
          </span>
        </Box>
      </Box>

      {/* Success Rate */}
      <Box
        sx={{
          mt: "auto",
          pt: 1.5,
          borderTop: `1px solid ${dividerColor}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Success Rate
        </Typography>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: successRateColor, lineHeight: 1.2, fontSize: "1.5rem" }}
        >
          {successRate}%
        </Typography>
      </Box>
    </Box>
  );
};

export default ComparisonDashboardCard;
